// @flow
import type {Node} from 'react'
import React, {Suspense, useCallback, useState} from 'react'
import {QueryRenderer, graphql} from 'react-relay'
import type {GraphQLTaggedNode} from 'react-relay'

import environment from 'environment'
import logger from 'utils/MiscUtils/main'
import preloadedLazy from 'utils/MiscUtils/preloadedLazy'
import ModalLoader from 'components/ui/ModalLoader'
import QuickAddButton from 'components/SideBar/ui/QuickAddButton'

import type {
  AgendaItemOpenerIslandQueryResponse as Response,
} from './__generated__/AgendaItemOpenerIslandQuery.graphql'

// GF-2954: the agenda-item quick-add opener is one of the two modal-opener
// islands kept in React (the modal itself is a React surface). The modal's
// data is fetched on demand — the query only runs once the button is clicked,
// on its own per-section query (rule-6 exception ruled on GF-2954).
const LazyCreateAgendaItemModal = preloadedLazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "CreateAgendaItemModal" */
  'components/agendaItems/CreateAgendaItemModal'
))

export const agendaItemOpenerIslandQuery: GraphQLTaggedNode = graphql`
  query AgendaItemOpenerIslandQuery($orgDatabaseId: String!) {
    organization(databaseId: $orgDatabaseId) {
      ...CreateAgendaItemModal_organization
    }
  }
`

type Props = $ReadOnly<{
  orgDatabaseId: string,
}>

type RendererProps = $ReadOnly<{
  // eslint-disable-next-line react/no-unused-prop-types
  error: ?Object,
  // eslint-disable-next-line react/no-unused-prop-types
  props: ?Response,
  ...
}>

function AgendaItemOpenerIsland({orgDatabaseId}: Props): Node {
  const [isOpen, setIsOpen] = useState(false)

  const close = useCallback(() => setIsOpen(false), [])
  const open = useCallback(() => setIsOpen(true), [])

  const renderModal = useCallback(({props: response, error}: RendererProps) => {
    if (error) {
      logger.warn('error:', error)
      return null
    }

    if (!response?.organization)
      return <ModalLoader />

    return (
      <Suspense fallback={<ModalLoader />}>
        <LazyCreateAgendaItemModal
          close={close}
          organization={response.organization}
        />
      </Suspense>
    )
  }, [close])

  return (
    <React.Fragment>
      <QuickAddButton
        onClick={open}
        alt={I18n.t('side_nav.tension_processing.add_agenda_item')}
      />
      {isOpen && (
        <QueryRenderer
          environment={environment}
          fetchPolicy="store-and-network"
          query={agendaItemOpenerIslandQuery}
          variables={{orgDatabaseId}}
          render={renderModal}
        />
      )}
    </React.Fragment>
  )
}

export default AgendaItemOpenerIsland
